// src/pages/Integrations/Integrations.tsx

import React, { useEffect, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import {
	Row, Col, Card, CardHeader, CardBody,
	Button, Badge, Spinner, Alert
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch, Action } from "@reduxjs/toolkit";

import ConfirmConnectModal from "../Actions/ConfirmConnectModal";
import ApiKeyModal from "../Actions/ApiKeyModal";
import DeleteAccountModal from "../Actions/DeleteAccountModal";

import {
	listAppsRequest,
	installRequest,
	oauthInitiateRequest,
	uninstallRequest
} from "store/integrations/action";
import type { AppDTO, AppVersionDTO, InstallationSummaryDTO } from "store/integrations/types";
import type { ApplicationState } from "store";
import IntegrationsSkeleton from "./IntegrationsSkeleton";
import IntegrationsError from "./IntegrationsError";
import IntegrationsEmpty from "./IntegrationsEmpty";
import DnsConnectModal from "../Actions/DnsConnectModal";

const Integrations: React.FC<WithTranslation> = ({ t }) => {
	const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
	const navigate = useNavigate();
	const tenantId = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id!);

	const { apps, loadingApps, errorApps } = useSelector((s: ApplicationState) => ({
		apps: s.integration.apps,
		loadingApps: s.integration.loadingApps,
		errorApps: s.integration.errorApps
	}));

	// Modal state
	const [confirmApp, setConfirmApp] = useState<AppDTO | null>(null);
	const [apiKeyApp, setApiKeyApp] = useState<AppDTO | null>(null);
	const [dnsModal, setDnsModal] = useState<AppDTO | null>(null);
	const [deleteInst, setDeleteInst] = useState<InstallationSummaryDTO | null>(null);
	const [oauthLoading, setOauthLoading] = useState(false);

	useEffect(() => {
		if (tenantId) {
			dispatch(listAppsRequest(tenantId));
		}
	}, [dispatch, tenantId]);

	if (loadingApps) return <IntegrationsSkeleton />;
	if (errorApps) return <IntegrationsError error={'errorApps'} onRetry={() => dispatch(listAppsRequest(tenantId))} />;
	if (!apps.length) return <IntegrationsEmpty />;

	const onConnectClick = (app: AppDTO) => {
		const authType = app.version_metadata.authType || "none";
		if (authType === "oauth") {
			setConfirmApp(app);
		} else if (authType === "apikey") {
			setApiKeyApp(app);
		} if (authType === "dns") {
			setDnsModal(app);
		} else {
			dispatch(installRequest(
				tenantId,
				app.latest_version_id,
				"{}",
				() => dispatch(listAppsRequest(tenantId)),
				console.error
			));
		}
	};

	const onConfirmOauth = () => {
		if (!confirmApp) return;
		setOauthLoading(true);
		dispatch(oauthInitiateRequest(
			tenantId,
			confirmApp.id,
			(url: string) => window.location.href = url,
			(err: any) => { console.error(err); setOauthLoading(false); setConfirmApp(null); }
		));
	};

	const onSubmitApiKey = (config: Record<string, string>) => {
		if (!apiKeyApp) return;
		setApiKeyApp(null);
		dispatch(installRequest(
			tenantId,
			apiKeyApp.latest_version_id,
			config?.name,
			JSON.stringify(config),
			() => dispatch(listAppsRequest(tenantId)),
			console.error
		));
	};

	const onDisconnectClick = (inst: InstallationSummaryDTO) => {
		setDeleteInst(inst);
	};
	const onConfirmDisconnect = () => {
		if (!deleteInst) return;
		dispatch(uninstallRequest(
			tenantId,
			deleteInst.id,
			() => {
				setDeleteInst(null);
				dispatch(listAppsRequest(tenantId));
			},
			() => setDeleteInst(null)
		));
	};

	console.log("apiKeyApp>>>", apiKeyApp)

	return (
		<div>
			{/* Modals */}
			{confirmApp && (
				<ConfirmConnectModal
					show
					onCloseClick={() => setConfirmApp(null)}
					selectedPlatform={{
						app: { name: confirmApp.name, logo_url: confirmApp.logo_url },
						config: {}
					}}
					onConfirm={onConfirmOauth}
					loading={oauthLoading}
				/>
			)}
			{apiKeyApp && (() => {
				// look up the first version for this app
				const credentials = apiKeyApp?.version_metadata?.credentials as any[] | undefined;
				if (!credentials?.length) return null;

				return (
					<ApiKeyModal
						show
						appName={apiKeyApp.name}
						installName={`New ${apiKeyApp.name} Connection`}
						credentials={credentials}
						onClose={() => setApiKeyApp(null)}
						onSubmit={onSubmitApiKey}
					/>
				);
			})()}

			{dnsModal && <DnsConnectModal
				show={!!dnsModal}
				app={dnsModal}
				fields={dnsModal?.version_metadata?.authFlow?.fields || []}
				operations={dnsModal?.version_metadata?.authFlow?.operations || {}}
				installRequires={dnsModal?.version_metadata?.authFlow?.installRequires || []}
				tenantId={tenantId}
				onClose={() => setDnsModal(null)}
				onInstall={async (domain: string) => {
					await dispatch(installRequest(
						tenantId,
						dnsModal.latest_version_id,
						domain,
						JSON.stringify({ domain }),
						() => { setDnsModal(null); dispatch(listAppsRequest(tenantId)); },
						(err: { message: string; }) => alert("Failed: " + err?.message)
					));
				}}
			/>}

			{deleteInst && (
				<DeleteAccountModal
					show
					onClose={() => setDeleteInst(null)}
					record={deleteInst}
					onConfirm={onConfirmDisconnect}
				/>
			)}

			<p className="text-muted mb-4">
				{t("integrations.subtitle", "Connect your apps to bring all your data into one central place.")}
			</p>

			<Row className="gx-4 gy-4">
				{apps.map(app => {
					const installs = app.installations || [];
					const activeInstalls = installs.filter(i => i.status === "ACTIVE");
					const pendingInstalls = installs.filter(i => i.status === "PENDING_SETUP");

					// decide primary action
					let primaryButton;
					if (activeInstalls.length > 0) {
						primaryButton = (
							<Button size="sm" color="success" onClick={() => navigate(`/integrations/${app.id}`)}>
								{t("action.manage", "Manage")}
							</Button>
						);
					} else if (pendingInstalls.length > 0) {
						primaryButton = (
							<Button size="sm" color="warning" onClick={() => navigate(`/integrations/${app.id}`)}>
								{t("action.continueSetup", "Continue setup")}
							</Button>
						);
					} else {
						primaryButton = (
							<Button size="sm" color="primary" onClick={() => onConnectClick(app)}>
								{t("action.connect", "Connect")}
							</Button>
						);
					}


					return (
						<Col key={app.id} xs={12} sm={6} md={4} lg={3}>
							<Card className="h-100 shadow-sm rounded">

								<CardHeader className="bg-light p-3">
									<div className="d-flex align-items-start">
										<img
											src={app.logo_url}
											alt={app.name}
											width={40}
											height={40}
											style={{ borderRadius: 4, flexShrink: 0 }}
										/>
										<div className="ms-3">
											{/* allow wrapping */}
											<h5 className="mb-1" style={{ whiteSpace: 'normal' }}>
												{app.name}
											</h5>
											<small className="text-muted">{app.developer}</small>
										</div>
									</div>
								</CardHeader>


								<CardBody className="d-flex flex-column p-3">
									<div
										className="d-flex flex-wrap mb-3"
										style={{ gap: '0.25rem' }}
									>
										{activeInstalls.length > 0 && (
											<Badge color="success" pill>
												{activeInstalls.length} {t("integrations.badge.connected", "Connected")}
											</Badge>
										)}
										{pendingInstalls.length > 0 && (
											<Badge color="warning" pill>
												{pendingInstalls.length} {t("integrations.badge.pending", "Pending")}
											</Badge>
										)}
									</div>
									<p
										className="text-secondary mb-4"
										style={{
											overflow: "hidden",
											display: "-webkit-box",
											WebkitLineClamp: 3,
											WebkitBoxOrient: "vertical"
										}}
									>
										{app.description}
									</p>
									<div className="d-flex gap-2">
										{primaryButton}
										<Button outline size="sm" color="primary"
											onClick={() => navigate(`/integrations/${app.id}`)}
										>
											{t("action.learnMore", "Learn More")}
										</Button>
									</div>
								</CardBody>
							</Card>
						</Col>
					);
				})}
			</Row>
		</div>
	);
};

export default withTranslation()(Integrations);
